<script>
  import * as d3 from "d3";
  import { onMount } from "svelte";

  export let data;

  let svg;
  let isMinutes = false;
  $: metric = isMinutes ? "total_minutes" : "total_streams";

  let hideEnglish = false;
  let hideInstrumental = false;

  let tooltip = null;
  let tooltipX = 0;
  let tooltipY = 0;
  let chartContainer;

  const width = 480;
  
  const barHeight = 28;
  const margin = { top: 20, right: 20, bottom: 20, left: 100 };

  $: filtered = data.filter(d => {
    if (hideEnglish && d.language === "English") return false;
    if (hideInstrumental && d.language === "Instrumental") return false;
    return true;
  });


  function draw() {
    const sorted = [...filtered]
      .sort((a, b) => b[metric] - a[metric]);

    const height = sorted.length * barHeight;
    const total = d3.sum(filtered, d => d[metric]);

    const isMobile = window.innerWidth <= 480;
    const widthFactor = isMobile ? 1 : 1.15;
    const dynamicWidth = isMobile ? 410 : 480;

    d3.select(svg).selectAll("*").remove();

    const x = d3
      .scaleLinear()
      .domain([0, d3.max(sorted, d => d[metric])])
      .range([0, (dynamicWidth * widthFactor) - margin.left - margin.right]);

    const y = d3
      .scaleBand()
      .domain(sorted.map(d => d.language))
      .range([0, height])
      .padding(0.2);

    const svgEl = d3.select(svg)
    .attr(
      "viewBox",
      `0 0 ${dynamicWidth * widthFactor} ${(height + margin.top + margin.bottom)}`
      )
    .attr("preserveAspectRatio", "xMidYMid meet")
    .style("width", "100%")
    .style("height", "auto");

    const g = svgEl
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const yAxis = g.append("g")
      .call(d3.axisLeft(y));

    const xAxis = g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5));

    yAxis.selectAll(".domain")
         .attr("opacity", "0");

    xAxis.selectAll(".domain")
         .attr("fill", "#202020");

    yAxis.selectAll("line")
         .attr("opacity", "0")

    xAxis.selectAll("line")
         .attr("opacity", "0");

    g.selectAll("rect")
      .data(sorted)
      .enter()
      .append("rect")
      .attr("y", d => y(d.language))
      .attr("height", y.bandwidth())
      .attr("width", d => x(d[metric]))
      .attr("fill", "#1DB954")
      .attr("rx", 5)
      .on("mouseenter", function (event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("fill", "#4DFF8D")
          .style("filter", "drop-shadow(0 0 6px rgba(29,185,84,0.7))");

          const percentage = ((d[metric] / total) * 100).toFixed(1);

          tooltip.style.opacity = 1;
          tooltip.innerHTML = `
          <strong>${d.language}</strong><br/>
          ${isMinutes ? "Minutes" : "Streams"}: 
          ${d[metric].toLocaleString()}<br/>
          ${percentage}% of total
          `;

          const [x, y] = d3.pointer(event);

          if (isMobile) {
            tooltip.style.left = `${x - 140}px`;
          } 
      })

      .on("mousemove", function (event) {
        tooltip.style.left = event.pageX + 12 + "px";
        tooltip.style.top = event.pageY - 28 + "px";
      })

      .on("mouseleave", function () {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("fill", "#1DB954")
          .style("filter", "none");
        tooltip.style.opacity = 0;
      });
    }

  onMount(draw);  
$: if (data && metric && filtered) {
  draw();
}

</script>

<div class="lang-wrapper">
  <div class="lang-header">
    <h2>Most streamed languages</h2>
    <div class="controls">
      <div class="metric-switch">
        <label class="switch">
          <input type="checkbox" bind:checked={isMinutes} />
          <span class="slider">
            <span class="text-wrapper">
              <span class="text streams">By streams</span>
              <span class="text minutes">By minutes</span>
            </span>
          </span>
        </label>
      </div>
      <div class="remove">
      <label>
        <input type="checkbox" bind:checked={hideEnglish} />
        <span>Hide English</span>
      </label>
      <label>
        <input type="checkbox" bind:checked={hideInstrumental} />
        <span>Hide Instrumental</span>
      </label>
      </div>
    </div>
  </div>
  <div class="lang-chart">
    <svg bind:this={svg}></svg>
  </div>
  <div class="tooltip" bind:this={tooltip}></div>
</div>

<style>

  h2 {
    color: #FFFFFF;
    font-family: "Outfit", sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0;
    /*white-space: nowrap;*/
  }

  svg {
    width: 100%;
    height: auto;
  }

  .lang-wrapper {
    background: #202020;
    padding: 1.25rem;
    border-radius: 16px;
    display: flex;
    flex-direction: column;    
    box-shadow: 0 0 8px rgba(29, 185, 84, 0.4);

    min-width: 320px;
    min-height: 0px;
  }

  .lang-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .lang-chart {
    max-height: 320px;
    background: #0F0F0F;
    overflow-y: auto;
    border-radius: 12px;
    width: 100%;
    min-height: 0px;
  }

  .controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
  }

  label {
    font-family: "Outfit", sans-serif;
    font-size: 12px;
    color: #FFFFFF;
  }

  :global(.tick text) {
    font-family: "Outfit", sans-serif;
    font-size: 12px;
    fill: #FFFFFF;
  }

  :global(.axis-label) {
    font-weight: 600;
    fill: #222;
  }

  .metric-switch {
    display: flex;
    align-items: center;
  }

  .switch input {
    display: none;
  }

  .slider {
    display: inline-flex;
    background: #2a2a2a;
    color: #FFFFFF;
    font-family: "Outfit", sans-serif;
    font-size: 0.75rem;
    border-radius: 16px;
    padding: 6px 12px;
    cursor: pointer;
    overflow: hidden;
    height: 16px;
    white-space: nowrap;
  }

  .slider:hover {
    color: #1DB954;
    background: #303030;
    transition: all 0.25s ease;
    box-shadow: 0 0 8px rgba(29, 185, 84, 0.4);
  }

  .text-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: transform 0.25s ease;
  }

  .text {
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .switch input:checked + .slider .text-wrapper {
    transform: translateY(-26px);
  }

  .remove {
    display: flex;
    gap: 0.5rem;
    white-space: nowrap;
  }

  .remove span:hover {
    color: #FFFFFF;
    background-color: #303030;
    transition: all 0.4s ease;
  }

  .remove label {
    position: relative;
    cursor: pointer;
  }

  .remove input {
    display: none;
  }

  .remove span {
    padding: 6px 12px;
    border-radius: 16px;
    font-family: "Outfit", sans-serif;
    font-size: 0.75rem;
    background: #2a2a2a;
    color: #bbb;
    transition: all 0.2s ease;
  }

  .remove input:checked + span {
    background: #1DB954;
    color: white;
  }

  .tooltip {
    position: absolute;
    background: #0F0F0F;
    color: #FFFFFF;
    padding: 8px 12px;
    border-radius: 8px;
    font-family: "Outfit", sans-serif;
    font-size: 12px;
    pointer-events: none;
    border: 1px solid #1DB954;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15),
                0 0 6px rgba(29,185,84,0.7);
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background: #3F3F3F;
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    .lang-wrapper {
      min-width: 0;
    }

    .lang-header {
      flex-direction: column;
      align-items: flex-start; 
      width: 100%;
    }
    
    h2 {
      font-size: 1.125rem;
      margin-top: 0;
    }

    .controls {
      align-self: flex-end;
    }

    .lang-chart {
      max-height: 200px;
    }

    .remove span {
      font-size: 0.625rem;
      font-weight: 400;
    }

    .slider {
      font-size: 0.625rem;
      font-weight: 400;
      padding: 5px 12px;
    }

    .switch input:checked + .slider .text-wrapper {
      transform: translateY(-24px);
    }
  }

</style>

